import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, Cpu, Target, TrendingUp, TrendingDown, Minus, ScanLine, Zap, Infinity as InfinityIcon, ArrowRight, PlayCircle } from "lucide-react";

const SAMPLES = [
  { id: "s1", label: "EUR/USD · Liquidity Sweep", url: "https://images.unsplash.com/photo-1689732888407-310424e3a372?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxmb3JleCUyMHRyYWRpbmclMjBmaW5hbmNpYWwlMjBtYXJrZXQlMjB0cmFkaW5nJTIwY2hhcnRzfGVufDB8fHx8MTc4OTkzMTQ3OXww&ixlib=rb-4.1.0&q=85" },
  { id: "s2", label: "XAU/USD · Gold Breakout", url: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxmb3JleCUyMHRyYWRpbmclMjBmaW5hbmNpYWwlMjBtYXJrZXQlMjB0cmFkaW5nJTIwY2hhcnRzfGVufDB8fHx8MTc4OTkzMTQ3OXww&ixlib=rb-4.1.0&q=85" },
  { id: "s3", label: "GBP/JPY · Consolidation", url: "https://images.unsplash.com/photo-1560221328-12fe60f83ab8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHx0cmFkZXIlMjBjYW5kbGVzdGljayUyMGNoYXJ0JTIwc3RvY2slMjBtYXJrZXQlMjBzY3JlZW58ZW58MHx8fHwxNzg5OTMxNDc5fDA&ixlib=rb-4.1.0&q=85" },
];

const STAGES = ["Reading candlesticks", "Identifying liquidity sweeps", "Mapping support & resistance", "Computing risk : reward", "Finalizing verdict"];

const STRATEGY_OPTS = [
  { v: "price_action", label: "Price Action", desc: "Naked chart — candles, S/R, supply & demand" },
  { v: "scalping", label: "Scalping", desc: "M1–M15 quick intraday moves, tight stops" },
  { v: "swing", label: "Swing Trading", desc: "H4–D1 multi-day holds, wider targets" },
  { v: "smc", label: "Smart Money Concepts", desc: "Order blocks, liquidity sweeps, FVG, BOS/CHoCH" },
  { v: "breakout", label: "Breakout", desc: "Consolidation ranges, momentum expansion" },
];

const BiasBadge = ({ bias, confidence }) => {
  const map = {
    BULLISH: { c: "text-gain", bg: "bg-gain/12 border-gain/30", Icon: TrendingUp },
    BEARISH: { c: "text-loss", bg: "bg-loss/12 border-loss/30", Icon: TrendingDown },
    NEUTRAL: { c: "text-amber", bg: "bg-amber/12 border-amber/30", Icon: Minus },
  };
  const s = map[bias] || map.NEUTRAL;
  return (
    <span data-testid="ai-trade-verdict-badge" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border font-data text-xs font-semibold ${s.bg} ${s.c}`}>
      <s.Icon className="h-4 w-4" /> {bias} · {confidence}%
    </span>
  );
};

const QuotaMeter = ({ quota, onUpgrade }) => {
  if (!quota) return null;
  if (quota.unlimited) {
    return (
      <div data-testid="quota-meter" className="flex items-center gap-2 rounded-lg border border-brand/25 bg-brand/8 px-3.5 py-2.5">
        <InfinityIcon className="h-4 w-4 text-brand shrink-0" />
        <span data-testid="quota-meter-text" className="font-data text-xs text-slate-200">Unlimited analyses today</span>
        <span className="ml-auto font-data text-[9px] uppercase tracking-widest text-brand">{quota.plan}</span>
      </div>
    );
  }
  const remaining = quota.remaining ?? 0;
  const limit = quota.limit ?? 0;
  const out = remaining <= 0;
  const pct = limit ? Math.max(0, Math.min(100, (remaining / limit) * 100)) : 0;
  return (
    <div data-testid="quota-meter" className={`rounded-lg border px-3.5 py-2.5 ${out ? "border-loss/30 bg-loss/8" : "border-white/10 bg-[#0C1017]"}`}>
      <div className="flex items-center gap-2">
        <Zap className={`h-4 w-4 shrink-0 ${out ? "text-loss" : "text-amber"}`} />
        <span data-testid="quota-meter-text" className={`font-data text-xs ${out ? "text-loss" : "text-slate-200"}`}>
          {out ? "Daily limit reached" : `${remaining} of ${limit} analyses left today`}
        </span>
        <span className="ml-auto font-data text-[9px] uppercase tracking-widest text-slate-500">{quota.plan} plan</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${out ? "bg-loss" : "bg-amber"}`} style={{ width: `${pct}%` }} />
      </div>
      {out && (
        <button
          data-testid="quota-upgrade-nudge"
          onClick={onUpgrade}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-brand hover:bg-brand-hover text-white font-data text-[11px] font-semibold uppercase tracking-wider py-2 transition-colors"
        >
          Upgrade for more analyses <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export const ChartAnalyzer = ({ onRequireAuth, onUpgrade, embedded = false }) => {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [imageB64, setImageB64] = useState(null);
  const [sampleUrl, setSampleUrl] = useState(null);
  const [strategy, setStrategy] = useState("price_action");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const fileRef = useRef(null);

  const loadQuota = useCallback(async () => {
    if (!user) { setQuota(null); return; }
    try {
      const { data } = await api.get("/analyze/quota");
      setQuota(data);
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  const handleFile = (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) {
      toast.error("Please upload a PNG, JPG or WEBP image");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setImageB64(reader.result);
      setSampleUrl(null);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const pickSample = (s) => {
    setPreview(s.url);
    setSampleUrl(s.url);
    setImageB64(null);
    setResult(null);
  };

  const clear = () => { setPreview(null); setImageB64(null); setSampleUrl(null); setResult(null); };

  const analyze = async () => {
    if (!user) { onRequireAuth(); return; }
    if (!preview) { toast.error("Upload a chart or pick a sample first"); return; }
    setLoading(true);
    setResult(null);
    setStage(0);
    const timer = setInterval(() => setStage((s) => (s < STAGES.length - 1 ? s + 1 : s)), 1200);
    try {
      const payload = { strategy };
      if (imageB64) payload.image_base64 = imageB64;
      else payload.image_url = sampleUrl;
      const { data } = await api.post("/analyze/chart", payload);
      setResult(data.result);
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed");
    } finally {
      clearInterval(timer);
      setLoading(false);
      loadQuota();
    }
  };

  return (
    <section id="analyzer" className={embedded ? "relative" : "relative py-20 lg:py-28 border-b border-white/5"}>
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}>
        {!embedded && (
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Netroy Chart Analyzer</h2>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: input */}
          <div className="lg:col-span-5 space-y-5">
            <div
              data-testid="chart-upload-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="relative cursor-pointer rounded-xl border-2 border-dashed border-white/12 hover:border-brand/50 bg-[#0C1017] transition-colors duration-150 overflow-hidden"
            >
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="chart preview" className="w-full h-64 object-cover" />
                  <button
                    onClick={(e) => { e.stopPropagation(); clear(); }}
                    className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/70 border border-white/20 grid place-items-center hover:bg-loss/80"
                    data-testid="chart-clear-button"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ) : (
                <div className="py-16 flex flex-col items-center text-center px-6">
                  <UploadCloud className="h-10 w-10 text-brand mb-3" />
                  <p className="text-sm text-slate-200 font-medium">Drop chart image or click to upload</p>
                  <p className="font-data text-[11px] text-slate-500 mt-1">PNG · JPG · WEBP</p>
                </div>
              )}
              <input ref={fileRef} data-testid="chart-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>

            <div>
              <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Quick samples</p>
              <div className="grid grid-cols-3 gap-2">
                {SAMPLES.map((s) => (
                  <button
                    key={s.id}
                    data-testid="chart-sample-select-btn"
                    onClick={() => pickSample(s)}
                    className={`group relative rounded-lg overflow-hidden border transition-colors duration-150 ${sampleUrl === s.url ? "border-brand" : "border-white/10 hover:border-brand/40"}`}
                  >
                    <img src={s.url} alt={s.label} className="h-16 w-full object-cover opacity-80 group-hover:opacity-100" />
                    <span className="block px-1.5 py-1 text-[9px] font-data text-slate-300 bg-[#0C1017] truncate">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Strategy style</p>
                <Select value={strategy} onValueChange={setStrategy}>
                  <SelectTrigger data-testid="chart-strategy-select" className="bg-[#0C1017] border-white/10 text-white h-11">
                    <SelectValue>{STRATEGY_OPTS.find((o) => o.v === strategy)?.label}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                    {STRATEGY_OPTS.map((o) => (
                      <SelectItem key={o.v} value={o.v} textValue={o.label} className="py-2">
                        <div className="flex flex-col">
                          <span className="text-sm text-white">{o.label}</span>
                          <span className="font-data text-[10px] text-slate-500 mt-0.5">{o.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {user && <QuotaMeter quota={quota} onUpgrade={onUpgrade} />}

            <Button
              data-testid="chart-start-analysis-btn"
              onClick={analyze}
              disabled={loading}
              className="w-full h-12 bg-brand hover:bg-brand-hover text-white font-semibold gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />}
              {loading ? "Analyzing…" : user ? "Run Strong Verdict" : "Sign in to Analyze"}
            </Button>
          </div>

          {/* Right: result */}
          <div className="lg:col-span-7">
            <div data-testid="ai-analysis-result-panel" className="card-surface rounded-xl p-6 min-h-[420px]">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8">
                    <div className="flex items-center gap-2 mb-6">
                      <ScanLine className="h-5 w-5 text-brand animate-pulse" />
                      <span className="font-data text-sm text-slate-300">Netroy Engine working…</span>
                    </div>
                    <div className="space-y-3">
                      {STAGES.map((st, i) => (
                        <div key={i} className={`flex items-center gap-3 transition-opacity duration-300 ${i <= stage ? "opacity-100" : "opacity-30"}`}>
                          {i < stage ? <div className="h-4 w-4 rounded-full bg-gain grid place-items-center text-[8px] text-black">✓</div>
                            : i === stage ? <Loader2 className="h-4 w-4 text-brand animate-spin" />
                            : <div className="h-4 w-4 rounded-full border border-white/20" />}
                          <span className="font-data text-sm text-slate-300">{st}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ) : result ? (
                  <motion.div key="result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <span className="font-data text-[11px] uppercase tracking-widest text-slate-500">{result.pair} · {result.timeframe}</span>
                        <div className="mt-1"><BiasBadge bias={result.bias} confidence={result.confidence} /></div>
                      </div>
                      <span className="font-data text-xs px-2.5 py-1 rounded bg-brand/12 text-brand border border-brand/25">{result.order_type}</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { k: "Entry", v: result.entry },
                        { k: "Stop Loss", v: result.stop_loss, c: "text-loss" },
                        { k: "TP 1", v: result.take_profit_1, c: "text-gain" },
                        { k: "TP 2", v: result.take_profit_2, c: "text-gain" },
                      ].map((m) => (
                        <div key={m.k} className="rounded-lg bg-[#0C1017] border border-white/8 p-3">
                          <div className="font-data text-[10px] uppercase tracking-widest text-slate-500">{m.k}</div>
                          <div className={`font-data text-base font-semibold mt-1 ${m.c || "text-white"}`}>{m.v}</div>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 rounded-lg bg-brand/5 border border-brand/20 p-3.5">
                      <Target className="h-5 w-5 text-brand shrink-0" />
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm text-slate-300">Risk : Reward</span>
                        <span className="font-data text-lg font-bold text-white">{result.risk_reward}</span>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Support</p>
                        <ul className="space-y-1">{(result.support || []).map((s, i) => <li key={i} className="font-data text-sm text-gain">↳ {s}</li>)}</ul>
                      </div>
                      <div>
                        <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Resistance</p>
                        <ul className="space-y-1">{(result.resistance || []).map((s, i) => <li key={i} className="font-data text-sm text-loss">↳ {s}</li>)}</ul>
                      </div>
                    </div>

                    <div>
                      <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Price Action</p>
                      <p data-testid="ai-price-action" className="text-sm text-slate-300 leading-relaxed">{result.price_action || result.market_structure}</p>
                    </div>

                    {result.replay?.length > 0 && (
                      <div data-testid="price-action-replay">
                        <div className="flex items-center gap-2 mb-3">
                          <PlayCircle className="h-4 w-4 text-brand" />
                          <p className="font-data text-[11px] uppercase tracking-widest text-slate-500">Price-Action Replay</p>
                        </div>
                        <div className="relative pl-6">
                          <div className="absolute left-[9px] top-1 bottom-1 w-px bg-white/10" />
                          <div className="space-y-3">
                            {result.replay.map((step, i) => (
                              <motion.div
                                key={i}
                                data-testid="replay-step"
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.25 + i * 0.55, duration: 0.35 }}
                                className="relative"
                              >
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.25 + i * 0.55, type: "spring", stiffness: 300 }}
                                  className="absolute -left-6 top-0.5 h-[18px] w-[18px] rounded-full bg-brand/15 border border-brand/50 grid place-items-center font-data text-[9px] font-bold text-brand"
                                >
                                  {i + 1}
                                </motion.span>
                                <p className="text-sm text-slate-300 leading-relaxed">{step}</p>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="font-data text-[11px] uppercase tracking-widest text-slate-500 mb-2">Market Structure & Fibonacci</p>
                      <p className="text-sm text-slate-300">{result.market_structure}</p>
                      <p className="text-sm text-slate-400 mt-1">{result.fibonacci}</p>
                    </div>

                    <div className="rounded-lg bg-[#0C1017] border border-white/8 p-4">
                      <p className="text-sm text-slate-300 leading-relaxed">{result.summary}</p>
                    </div>

                    {result.key_observations?.length > 0 && (
                      <ul className="space-y-1.5">
                        {result.key_observations.map((o, i) => (
                          <li key={i} className="flex gap-2 text-sm text-slate-400"><span className="text-brand">·</span>{o}</li>
                        ))}
                      </ul>
                    )}
                    <p className="font-data text-[10px] text-slate-600 pt-2 border-t border-white/5">Educational analysis — not financial advice.</p>
                  </motion.div>
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[380px] flex flex-col items-center justify-center text-center">
                    <div className="h-14 w-14 rounded-xl border border-white/10 bg-white/5 grid place-items-center mb-4">
                      <Cpu className="h-7 w-7 text-slate-500" />
                    </div>
                    <p className="text-slate-400 text-sm max-w-xs">Upload a chart or select a sample, then run the strong verdict to see a full institutional trade breakdown here.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
